import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Select,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import WoodModal from './WoodModal';
import ImportWoodModal from './ImportWoodModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

import { Wood } from '../../../../types/Wood';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

const WoodMaster: React.FC = () => {
  const { woodtypes, loaded } = useAppSelector(state => state.master);
  const [woods, setWoods] = useState<Wood[]>([]);
  const [selectedWoodType, setSelectedWoodType] = useState<number | null>(null);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Wood | null>(null);

  const { styles } = useStyle();
  const currentModule = useCurrentModule();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const cancel: PopconfirmProps['onCancel'] = () => {
    showError('Click on No');
  };

  /* ===================== API ===================== */

  const fetchWood = async (woodTypeId?: number | null) => {
    try {
      const url = woodTypeId
        ? `woods?wood_type=${woodTypeId}`
        : 'woods';

      const data = await apiRequest<Wood[]>({
        method: 'get',
        url,
      });

      setWoods(data);
    } catch (error) {
      showError('Failed to fetch Wood');
      console.error(error);
    }
  };



  useEffect(() => {
    fetchWood();
  }, []);

  /* ===================== HANDLERS ===================== */

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchWood(selectedWoodType);
  };

  const handleEditClick = (record: Wood) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchWood(selectedWoodType);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (wood_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-wood/${wood_id}`,
      });
      showSuccess('Wood deleted');
      fetchWood(selectedWoodType);
    } catch {
      showError('Failed to delete Wood');
    }
  };

  const handleStatusToggle = async (woodId: number) => {
    setWoods(prev =>
      prev.map(w =>
        w.wood_id === woodId
          ? { ...w, wood_status: w.wood_status ? 0 : 1 }
          : w
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-wood/${woodId}`,
      });
      showSuccess('Wood status updated');
    } catch {
      showError('Failed to update status');
    }
  };

  const handleTableChange: TableProps<Wood>['onChange'] = (
    _pagination,
    filters,
    sorter
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  /* ===================== COLUMNS ===================== */

  const columns: TableColumnsType<Wood> = [
    {
      title: 'Wood Name',
      dataIndex: 'wood_name',
      key: 'wood_name',
      sorter: (a, b) => a.wood_name.localeCompare(b.wood_name),
      sortOrder: sortedInfo.columnKey === 'wood_name' ? sortedInfo.order : null,
    },
    {
      title: 'Thickness',
      dataIndex: 'wood_thickness',
      key: 'wood_thickness',
    },
    {
      title: 'Length',
      dataIndex: 'wood_length',
      key: 'wood_length',
      render: (val, record) => record.wood_length ?? record.wood_lenth ?? val,
    },
    {
      title: 'Qty',
      dataIndex: 'wood_qty',
      key: 'wood_qty',
      render: (val, record) => record.wood_qty ?? record.qty ?? 0,
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_, record) => (
        <Space>
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checked={!!record.wood_status}
                onChange={() => handleStatusToggle(record.wood_id)}
              />
              <Button onClick={() => handleEditClick(record)}>
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Wood?"
              onConfirm={() => handleDelete(record.wood_id)}
              onCancel={cancel}
            >
              <Button danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  /* ===================== RENDER ===================== */

  return (
    <>
      <Space
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Wood</Breadcrumb.Item>
        </Breadcrumb>

        <Flex gap="small">
          {/* <Select
            allowClear
            placeholder="Select wood type"
            style={{ width: 200 }}
            onChange={value => setSelectedWoodType(value)}
            options={woodtypes.map(wt => ({
              label: wt.wood_name,
              value: wt.wood_id,
            }))}
          />
          <Button type="primary" onClick={handleSearch}>
            Search
          </Button> */}
          {currentModule?.right_add === 'Y' && (
            <>
              <Button icon={<UploadOutlined />} onClick={() => setIsImportModalVisible(true)}>
                Import Woods
              </Button>
              <Button type="primary" onClick={() => setIsModalVisible(true)}>
                Add Wood
              </Button>
            </>
          )}
        </Flex>
      </Space>

      <div
        style={{
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table
          className={styles.customTable}
          columns={columns}
          rowKey="wood_id"
          dataSource={woods.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          pagination={false}
          onChange={handleTableChange}
          scroll={{ y: 520 }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(
                  pagination.current * pagination.pageSize,
                  woods.length
                )}{' '}
                of {woods.length}
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={woods.length}
                showSizeChanger
                onChange={(page, pageSize) =>
                  setPagination({
                    current: pageSize !== pagination.pageSize ? 1 : page,
                    pageSize,
                  })
                }
              />
            </Flex>
          )}
        />
      </div>

      <WoodModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />

      <ImportWoodModal
        visible={isImportModalVisible}
        onClose={() => setIsImportModalVisible(false)}
        onSuccess={() => fetchWood(selectedWoodType)}
      />
    </>
  );
};

export default WoodMaster;
